"use client"

import type React from "react"
import { type FormEvent, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import {
  type FeedbackStatus,
  type IRateAssignment,
  defaultRatingAssignment,
} from "../../../shared/models/three-sixty-feedback-models/RateAssignments"
import { distributeAndSendFeedback } from "../ratings/functions"
import type { IUser } from "../../../shared/models/User"
import MODAL_NAMES from "../../dialogs/ModalName"
import Modal from "../../../shared/components/Modal"
import EmailUsersModal from "../../dialogs/email-all-users/EmailUsersModal"
import { MAIL_EMAIL } from "../../../shared/functions/mailMessages"
import { useNavigate, useParams } from "react-router-dom"
import AdminRatingAssignmentManager from "./admin-rating-assignment-manager"
import {
  Calendar,
  Clock,
  Users,
  ArrowLeft,
  Play,
  Square,
  Trash2,
  RotateCcw,
  CheckCircle,
  AlertCircle,
} from "lucide-react"

// Delete Confirmation Modal Component
interface DeleteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  isDeleting: boolean
  confirmButtonText?: string
  confirmButtonIcon?: React.ReactNode
  confirmButtonColor?: "red" | "yellow"
}

const DeleteConfirmationModal: React.FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isDeleting,
  confirmButtonText = "Delete",
  confirmButtonIcon = <Trash2 className="h-4 w-4" />,
  confirmButtonColor = "red",
}) => {
  if (!isOpen) return null

  const buttonColorClasses =
    confirmButtonColor === "red" ? "bg-red-600 hover:bg-red-700" : "bg-yellow-600 hover:bg-yellow-700"

  const iconColorClasses = confirmButtonColor === "red" ? "bg-red-100 text-red-600" : "bg-yellow-100 text-yellow-600"

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-full ${iconColorClasses}`}>{confirmButtonIcon}</div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <p className="text-gray-600 mb-6">{message}</p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${buttonColorClasses}`}
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                {confirmButtonIcon}
                {confirmButtonText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Success Modal Component
interface SuccessModalProps {
  isOpen: boolean
  onClose: () => void
  message: string
}

export const SuccessModal: React.FC<SuccessModalProps> = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-full">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Success</h3>
        </div>

        <p className="text-gray-600 mb-6">{message}</p>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}

// Error Modal Component for validation errors
interface ErrorModalProps {
  isOpen: boolean
  onClose: () => void
  message: string
}

const ErrorModal: React.FC<ErrorModalProps> = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Error</h3>
        </div>

        <p className="text-gray-600 mb-6">{message}</p>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}

const FeedbackReview360 = observer(() => {
  const { api, store } = useAppContext()
  const today = new Date()
  const [session, setSession] = useState<IRateAssignment>({
    ...defaultRatingAssignment,
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState("midterm")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [revertModalOpen, setRevertModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)
  const [isReverting, setIsReverting] = useState(false)

  const navigate = useNavigate()
  const term = useParams()
  const termId = term.termId

  const users = store.user.all.map((user) => user.asJson)
  const usersIds = store.user.all.map((user) => user.asJson.uid)

  const [submitType, setSubmitType] = useState<
    "UPDATE_SESSION" | "START_SESSION" | "END_SESSION" | "DELETE_SESSION" | "REVERT_SESSION"
  >("START_SESSION")

  const generateFeedbackLink = (raterId: string) => {
    return `https://ecbdevelopment-9eafc.web.app/questionnaire/combined/${raterId}`
  }

  const getEmail = (raterId: string, user: IUser) => {
    const emails: string[] = []
    if (raterId === user.uid) {
      emails.push(user.email || "")
    }
    return emails
  }

  const processedEmails: string[] = []
  const sendFeedbackLink = async (raterId: string, feedbackLink: string, users: IUser) => {
    const emails = ["systems@lotsinsights.com"]
    // const emails = getEmail(raterId, users)
    for (const email of emails) {
      if (!processedEmails.includes(email)) {
        const message = `Welcome to 360-Feedback! Click the following link to participate: ${feedbackLink}`
        const subject = `ECB-360-Feedback Participation Invitation`
        try {
          console.log("Sending email to:", email)
          await api.mail.sendMail([email], MAIL_EMAIL, [], subject, message)
          processedEmails.push(email)
        } catch (error) {
          console.log("Error sending email:", error)
        }
      }
    }
  }

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setSuccessModalOpen(true)
    setTimeout(() => setSuccessModalOpen(false), 3000)
  }

  const showError = (message: string) => {
    setErrorMessage(message)
    setErrorModalOpen(true)
  }

  const hasActiveSession = () => {
    const allAssignments = store.ratingAssignments.all.map((assignment) => assignment.asJson)
    return allAssignments.some((assignment) => assignment.isActive || assignment.status === "In Progress")
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitType === "START_SESSION") {
      onStartSession()
    } else if (submitType === "END_SESSION") {
      onEndSession()
    } else if (submitType === "UPDATE_SESSION") {
      onUpdateSession()
    } else if (submitType === "DELETE_SESSION") {
      setDeleteModalOpen(true)
    } else if (submitType === "REVERT_SESSION") {
      setRevertModalOpen(true)
    }
  }

  const onDeleteSession = async () => {
    setIsDeleting(true)
    try {
      await api.ratingAssignments.delete(session)
      if (session.description) {
        await Promise.all([
          api.leadershipRating.deleteAllLeadershipRatings(usersIds, session.description),
          api.valueRating.deleteAllValueRatings(usersIds, session.description),
          api.templateRating.deleteAllTemplateRatings(session.description),
        ])
      }
      showSuccess("Session and all associated ratings deleted successfully!")
      navigate("/c/threesixty/home/feedback/reviews")
    } catch (error) {
      console.log("Error deleting session:", error)
    } finally {
      setIsDeleting(false)
      setDeleteModalOpen(false)
    }
  }

  const onRevertSession = async () => {
    if (hasActiveSession()) {
      showError("Cannot revert. Please close the active session first.")
      setRevertModalOpen(false)
      return
    }

    setIsReverting(true)
    try {
      const revertedSession = {
       ...session,
        midtermReview: {
          ...session.midtermReview,
          status: "In Progress" as FeedbackStatus,
        },
        isActive: true,
        status: "In Progress" as FeedbackStatus,
      }

      // await api.ratingAssignments.update(revertedSession)
      setSession(revertedSession)
      showSuccess("Session reverted to 'Not Started' status successfully!")
    } catch (error) {
      console.log("Error reverting session:", error)
    } finally {
      setIsReverting(false)
      setRevertModalOpen(false)
    }
  }

  const onUpdateSession = async () => {
    try {
      await api.ratingAssignments.update(session)
      showSuccess("Session updated successfully!")
    } catch (error) {
      console.log("Error updating session:", error)
    }
  }

  const sendQuestionnaire = async () => {
    setLoading(true)
    const feedbackData = await distributeAndSendFeedback(users)

    if (feedbackData) {
      setSession((prevState) => ({
        ...prevState,
        feedbackAssignments: feedbackData,
      }))

      for (const raterId in feedbackData) {
        const rateesId = feedbackData[raterId]
        for (const rateeId of rateesId) {
          const foundUser = users.find((user) => user.uid === rateeId)
          const feedbackLink = generateFeedbackLink(raterId)
          if (foundUser) await sendFeedbackLink(raterId, feedbackLink, foundUser)
        }
      }
      setLoading(false)
      showSuccess("Questionnaires sent successfully!")
    } else {
      console.error("No feedback assignments returned.")
      setLoading(false)
    }
  }

  const onStartSession = async () => {
    if (hasActiveSession()) {
      showError("A term is already in progress. Please complete the current term before starting a new one.")
      return
    }

    if (activeTab === "midterm") {
      sendQuestionnaire()
      const updatedSession = {
        ...session,
        midtermReview: {
          ...session.midtermReview,
          status: "In Progress" as FeedbackStatus,
        },
        isActive: true,
        status: "In Progress" as FeedbackStatus,
      }
      setSession(updatedSession)

      try {
        await api.ratingAssignments.update(updatedSession)
        showSuccess("Midterm session started successfully!")
      } catch (error) {
        console.log("Error starting session:", error)
      }
    } else if (activeTab === "final") {
      const updatedSession = {
        ...session,
        finalAssessment: {
          ...session.finalAssessment,
          status: "In Progress" as FeedbackStatus,
        },
        isActive: true,
        status: "In Progress" as FeedbackStatus,
      }
      setSession(updatedSession)

      try {
        await api.ratingAssignments.update(updatedSession)
        showSuccess("Final assessment started successfully!")
      } catch (error) {
        console.log("Error starting session:", error)
      }
    }
  }

  useEffect(() => {
    const saveRatingAssignments = async () => {
      try {
        await api.ratingAssignments.update(session)
      } catch (error) {
        console.log(error)
      }
    }

    if (session.status === "In Progress" && Object.keys(session.feedbackAssignments).length !== 0) {
      saveRatingAssignments()
    }
  }, [session])
  const onEndSession = async () => {
    if (activeTab === "midterm") {
      const updatedSession = {
        ...session,
        midtermReview: {
          ...session.midtermReview,
          status: "Completed" as FeedbackStatus,
        },
        isActive: false,
        status: "Completed" as FeedbackStatus,
      }
      setSession(updatedSession)

      try {
        await api.ratingAssignments.update(updatedSession)
        showSuccess("Midterm session completed successfully!")
      } catch (error) {
        console.log("Error ending session:", error)
      }
    } else if (activeTab === "final") {
      const updatedSession = {
        ...session,
        finalAssessment: {
          ...session.finalAssessment,
          status: "Completed" as FeedbackStatus,
        },
        isActive: false,
        status: "Completed" as FeedbackStatus,
      }
      setSession(updatedSession)

      try {
        await api.ratingAssignments.update(updatedSession)
        showSuccess("Final assessment completed successfully!")
      } catch (error) {
        console.log("Error ending session:", error)
      }
    }
  }

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedStartDate = new Date(e.target.value).getTime()
    if (selectedStartDate < today.getTime()) {
      alert("Start date cannot be in the past.")
      return
    }
    setSession({
      ...session,
      midtermReview: {
        ...(session.midtermReview || {}),
        startDate: selectedStartDate,
      },
    })
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedEndDate = new Date(e.target.value).getTime()
    if (session.midtermReview?.startDate && selectedEndDate <= session.midtermReview.startDate) {
      alert("End date cannot be earlier or equal the start date.")
      return
    }
    setSession({
      ...session,
      midtermReview: {
        ...(session.midtermReview || {}),
        endDate: selectedEndDate,
      },
    })
  }

  useEffect(() => {
    const load = async () => {
      try {
        await api.user.getAll()
        await api.ratingAssignments.getAll()
        const assignments = store.ratingAssignments.all.find((s) => s.asJson.id === termId)
        if (assignments !== undefined) {
          setSession(assignments.asJson)
        }
      } catch (error) {
        console.log("Error loading data:", error)
      }
    }
    load()
  }, [termId])

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    switch (status) {
      case "Completed":
        return `${baseClasses} bg-green-100 text-green-800`
      case "In Progress":
        return `${baseClasses} bg-yellow-100 text-yellow-800`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`
    }
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/c/threesixty/home/feedback/reviews")}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <div className="h-6 w-px bg-gray-300"></div>
                <h1 className="text-xl font-semibold text-gray-900">360° Feedback Management</h1>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8">
              {[
                { id: "midterm", label: "Midterm Review", icon: Calendar },
                { id: "final", label: "Final Assessment", icon: Clock },
                { id: "assignments", label: "Manage Assignments", icon: Users },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === "midterm" && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Midterm Review Configuration</h2>
                <p className="text-gray-600">Configure dates and manage the status of your midterm feedback review.</p>
              </div>

              <form onSubmit={onSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={
                        session.midtermReview?.startDate
                          ? new Date(session.midtermReview.startDate).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={handleStartDateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={
                        session.midtermReview?.endDate
                          ? new Date(session.midtermReview.endDate).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={handleEndDateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Status</label>
                    <div className="pt-2">
                      <span className={getStatusBadge(session?.midtermReview?.status || "Not Started")}>
                        {session?.midtermReview?.status || "Not Started"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  {session?.midtermReview?.status === "Not Started" && (
                    <button
                      type="submit"
                      onClick={() => setSubmitType("START_SESSION")}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Play className="h-4 w-4" />
                      {loading ? "Starting..." : "Start Session"}
                    </button>
                  )}

                  {session?.midtermReview?.status === "In Progress" && (
                    <button
                      type="submit"
                      onClick={() => setSubmitType("UPDATE_SESSION")}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Update Session
                    </button>
                  )}

                  <button
                    type="submit"
                    onClick={() => setSubmitType("END_SESSION")}
                    disabled={session?.midtermReview?.status === "Not Started"}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    <Square className="h-4 w-4" />
                    End Session
                  </button>

                  <button
                    type="submit"
                    onClick={() => setSubmitType("REVERT_SESSION")}
                    disabled={session?.midtermReview?.status === "Not Started"}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Revert Session
                  </button>

                  <button
                    type="submit"
                    onClick={() => setSubmitType("DELETE_SESSION")}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Session
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === "final" && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Final Assessment Configuration</h2>
                <p className="text-gray-600">
                  Configure dates and manage the status of your final feedback assessment.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={
                        session?.finalAssessment?.startDate
                          ? new Date(session.finalAssessment.startDate).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        setSession({
                          ...(session || {}),
                          finalAssessment: {
                            ...session.finalAssessment,
                            startDate: new Date(e.target.value).getTime(),
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={
                        session?.finalAssessment?.endDate
                          ? new Date(session.finalAssessment.endDate).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        setSession({
                          ...(session || {}),
                          finalAssessment: {
                            ...session.finalAssessment,
                            endDate: new Date(e.target.value).getTime(),
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Status</label>
                    <div className="pt-2">
                      <span className={getStatusBadge(session?.finalAssessment?.status || "Not Started")}>
                        {session?.finalAssessment?.status || "Not Started"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    type="submit"
                    onClick={() => setSubmitType("START_SESSION")}
                    disabled={session?.status === "In Progress"}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <Play className="h-4 w-4" />
                    Start Session
                  </button>

                  <button
                    type="submit"
                    onClick={() => setSubmitType("UPDATE_SESSION")}
                    disabled={session?.status !== "In Progress"}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Update Session
                  </button>

                  <button
                    type="submit"
                    onClick={() => setSubmitType("END_SESSION")}
                    disabled={session?.status !== "In Progress"}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    <Square className="h-4 w-4" />
                    End Session
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === "assignments" && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <AdminRatingAssignmentManager sessionId={session.id} />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={onDeleteSession}
        title="Delete Session"
        message="Are you sure you want to delete this session? This will permanently delete all associated value ratings, leadership ratings, and template ratings. This action cannot be undone."
        isDeleting={isDeleting}
        confirmButtonText="Delete"
        confirmButtonIcon={<Trash2 className="h-4 w-4" />}
        confirmButtonColor="red"
      />

      {/* Revert Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        onConfirm={onRevertSession}
        title="Revert Session"
        message="Are you sure you want to revert this session to 'Not Started' status? This will reset the session status but keep all existing data."
        isDeleting={isReverting}
        confirmButtonText="Revert"
        confirmButtonIcon={<RotateCcw className="h-4 w-4" />}
        confirmButtonColor="yellow"
      />

      {/* Success Modal */}
      <SuccessModal isOpen={successModalOpen} onClose={() => setSuccessModalOpen(false)} message={successMessage} />

      <ErrorModal isOpen={errorModalOpen} onClose={() => setErrorModalOpen(false)} message={errorMessage} />

      {/* Email Users Modal */}
      <Modal modalId={MODAL_NAMES.PERFORMANCE_REVIEW.MAIL_ALL_EMPLOYEES_MODAL}>
        <EmailUsersModal />
      </Modal>
    </ErrorBoundary>
  )
})

export default FeedbackReview360
